import React, { useState, useEffect } from 'react';
import { Download, FileText, User, Signature, Smartphone, CheckCircle, Video, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Home = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/get-settings`);
        const data = await response.json();
        if (data.status === 'success') {
          setSettings(data.data);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const getEmbedUrl = (url) => {
    if (!url) return 'https://www.youtube.com/embed/dQw4w9WgXcQ'; // Default fallback
    const videoIdMatch = url.match(/(?:v=|\/|shorts\/)([a-zA-Z0-9_-]{11})/);
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }
    return url;
  };

  const checklistItems = [
    {
      id: 1,
      text: 'Recent Passport size Photo - Clear photograph for swimming pool identity card (JPG or PNG only)',
      limit: 'Image size : max 50 KB',
      type: 'photo'
    },
    {
      id: 2,
      text: 'Medical Certificate - Valid health certificate from registered medical practitioner confirming fitness for swimming activities (PDF only)',
      limit: 'File size : max 300 KB',
      type: 'pdf',
      downloadUrl: settings?.medical_declaration
    },
    {
      id: 3,
      text: 'Declaration form (with father signature) - Essential document for parental consent for swimming pool access (PDF only)',
      limit: 'File size : max 300 KB',
      type: 'pdf',
      downloadUrl: settings?.father_declaration
    },
    {
      id: 4,
      text: 'Signature of Applicant - Clear and legible signature for swimming pool registration and record keeping (JPG or PNG only)',
      limit: 'Image size : max 50 KB',
      type: 'signature'
    },
    {
      id: 5,
      text: 'A valid Mobile Number is required. for feature communication.',
      limit: '',
      type: 'mobile'
    }
  ];

  const handleDownload = () => {
    if (settings?.rules_file) {
      window.open(settings.rules_file, '_blank');
    } else {
      const link = document.createElement('a');
      link.href = '/student-registration-guidelines.pdf';
      link.download = 'Student-Registration-Guidelines.pdf';
      link.click();
    }
  };

  const handleApply = () => {
    if (isAuthenticated) {
      navigate('/application');
    } else {
      navigate('/login');
    }
  };

  const renderIcon = (type) => {
    if (type === 'photo') {
      return <User size={28} />;
    }
    if (type === 'signature') {
      return <Signature size={28} />;
    }
    if (type === 'mobile') {
      return <Smartphone size={28} />;
    }
    return <FileText size={28} />;
  };

  return (
    <div className="pool-page">
      <Header />

      <main className="flex-grow-1">
        <div className="pool-main-card">
          <h1 className="pool-title mb-4" style={{fontSize:'25px', color:'#027bff'}}>বালুরঘাট পৌরসভা সুইমিং পুল</h1>
          
          <div className="pool-content-grid">
            <aside className="pool-actions-sidebar">
              <button 
                onClick={handleDownload}
                className="pool-action-btn pool-btn-primary"
              >
                <Download size={20} />
                <span>Download Rules</span>
              </button>
              {!isAuthenticated && (
                <button 
                  onClick={handleApply}
                  className="pool-action-btn pool-btn-success"
                >
                  <CheckCircle size={20} />
                  <span>Apply Online</span>
                </button>
              )}
              <button 
                className="pool-action-btn pool-btn-danger"
                onClick={() => setShowVideoModal(true)}
              >
                <Video size={20} />
                <span>Video Tutorial</span>
              </button>
            </aside>

            <div className="pool-checklist-section">
              <h2 className="pool-checklist-title">
                <CheckCircle size={24} />
                Keep The Following Items In Your Hand Before Applying:
              </h2>
              <table className="pool-checklist-table">
                <thead>
                  <tr>
                    <th style={{width: '50px'}}>No.</th>
                    <th>Requirements</th>
                    <th style={{width: '60px'}}>Icon</th>
                  </tr>
                </thead>
                <tbody>
                  {checklistItems.map((item) => (
                    <tr key={item.id}>
                      <td className="pool-item-number">{item.id}</td>
                      <td className="pool-item-content">
                        <div className="pool-item-text">{item.text}</div>
                        {item.limit && (
                          <div className="pool-item-limit">{item.limit}</div>
                        )}
                        {item.downloadUrl && (
                          <div className="pool-item-download mt-1">
                            <a 
                              href={item.downloadUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 py-0 px-2"
                              style={{fontSize: '0.8rem'}}
                            >
                              <Download size={14} />
                              Download Template
                            </a>
                          </div>
                        )}
                      </td>
                      <td className="pool-item-icon">{renderIcon(item.type)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Video Tutorial Modal */}
      {showVideoModal && (
        <div className="pool-video-modal-overlay" onClick={() => setShowVideoModal(false)}>
          <div className="pool-video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pool-video-modal-header">
              <h3>Video Tutorial</h3>
              <button 
                className="pool-video-modal-close"
                onClick={() => setShowVideoModal(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="pool-video-modal-content">
              <iframe
                width="100%"
                height="400"
                src={getEmbedUrl(settings?.youtube_video)}
                title="Swimming Pool Application Tutorial"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
